import { Flex } from 'antd';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector, useUserPermissions } from '@Hooks/index';
import { extractSiteSection } from '@Components/utils';
import { requestTypesMap } from '@Enum/RequestTypesMap';
import { currentSelectedRequest } from '@Features/store/selectors/manageRequestSelector';
import { FooterActions } from '@Enum/index';
import { FormType } from '@Components/MainForm/types';
import ApprovalButtons from './FooterButtons/ApprovalButtons';
import CreateButtons from './FooterButtons/CreateButtons';
import styles from './footer-button.module.css';
import EditButtons from './FooterButtons/EditButtons';
import MRButtons from './FooterButtons/MRButtons';
import EditSubmittedButtons from './FooterButtons/EditSubmittedButtons';

interface FooterButtonSectionProps {
  disabledSaveForm: boolean;
  disabledNextStep: boolean;
  formType: FormType;
  onFooterAction: (action: FooterActions, payload?: string) => void;
  approveBtnName?: string;
}

const FooterButtonSection: React.FC<FooterButtonSectionProps> = ({
  disabledSaveForm,
  disabledNextStep,
  formType,
  onFooterAction,
  approveBtnName,
}) => {
  const { pathname } = useLocation();
  const permissions = useUserPermissions();
  const selectedRequest = useAppSelector(currentSelectedRequest);
  const isEss = extractSiteSection(pathname) === 'ess';
  const requestType = requestTypesMap[selectedRequest];

  const onSaveAsDraft = useCallback(() => {
    onFooterAction(FooterActions.SAVE_AS_DRAFT);
  }, [onFooterAction]);

  const onDeleteRequest = useCallback(() => {
    onFooterAction(FooterActions.DELETE_REQUEST);
  }, [onFooterAction]);

  const onNext = useCallback(() => {
    onFooterAction(FooterActions.NEXT);
  }, [onFooterAction]);

  const onConfirm = useCallback(() => {
    onFooterAction(FooterActions.CONFIRM_REQUEST);
  }, [onFooterAction]);

  const onCancel = useCallback(() => {
    onFooterAction(FooterActions.CANCEL);
  }, [onFooterAction]);

  return (
    <Flex vertical>
      <Flex className={styles.formButtons}>
        {formType === 'approval' && (
          <ApprovalButtons
            onAction={onFooterAction}
            approveBtnName={approveBtnName}
          />
        )}
        {formType === 'create' && (
          <CreateButtons
            disabledSaveForm={disabledSaveForm}
            disabledSubmitForm={
              isEss && !permissions.can('Submit', requestType)
            }
            onSaveAsDraft={onSaveAsDraft}
          />
        )}
        {formType === 'edit-submitted' && (
          <EditSubmittedButtons
            disabledSaveForm={disabledSaveForm}
            disabledCancelForm={
              isEss && !permissions.can('Cancel', requestType)
            }
            onCancel={onCancel}
            onSaveAsDraft={onSaveAsDraft}
          />
        )}
        {formType === 'edit' && (
          <EditButtons
            disabledSaveForm={disabledSaveForm}
            disabledDeleteForm={
              isEss && !permissions.can('Delete', requestType)
            }
            disabledSubmitForm={
              isEss && !permissions.can('Submit', requestType)
            }
            onDeleteRequest={onDeleteRequest}
            onSaveAsDraft={onSaveAsDraft}
          />
        )}
        {formType === 'mr' && (
          <MRButtons
            disabledNextStep={disabledNextStep}
            disabledSaveForm={disabledSaveForm}
            onSaveAsDraft={onSaveAsDraft}
            onNext={onNext}
            onConfirm={onConfirm}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default FooterButtonSection;
